import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { CqcBadge } from "@/components/CqcBadge";
import { SITE, CARE_OPTIONS, CONTACT_TIME_OPTIONS } from "@/lib/site";
import { CONTACT_PAGE } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const officeMapQuery = (o) =>
  encodeURIComponent(`${o.line1}, ${o.line2}, ${o.line3} ${o.postcode}`);

const inputCls =
  "w-full bg-white border border-brand-line rounded-2xl px-5 py-3.5 text-brand-ink placeholder:text-brand-mute/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition";

const labelCls =
  "block text-xs uppercase tracking-[0.18em] font-semibold text-brand-mute mb-2";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  care_needed: CARE_OPTIONS[0],
  preferred_time: CONTACT_TIME_OPTIONS[0],
  message: "",
};

const selectChevron =
  " appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 24 24%22 stroke=%22%23A6248C%22 stroke-width=%221.8%22><path stroke-linecap=%22round%22 stroke-linejoin=%22round%22 d=%22M6 9l6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_1rem_center] pr-12";

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/enquiry`, form, { timeout: 20000 });
      if (res?.data?.status === "success") {
        setStatus("success");
        setForm(EMPTY_FORM);
      } else {
        setStatus("error");
        setErrorMsg(res?.data?.message || "Something went wrong. Please try again or call us.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "We couldn't send your enquiry just now. Please call us on 01302 481515.",
      );
    }
  };

  return (
    <div data-testid={CONTACT_PAGE.root}>
      {/* HERO */}
      <section className="relative pt-12 lg:pt-20 pb-12 bg-brand-cream overflow-hidden">
        <div className="absolute -top-24 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-soft blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Contact us
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-brand-ink">
              We'd love to{" "}
              <em className="not-italic text-brand">hear from you</em>.
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-brand-mute max-w-2xl leading-relaxed">
              Two offices serving the local community. Whether you're enquiring
              about care, asking about a current package, or interested in
              joining our team — get in touch and we'll be happy to help.
            </p>
          </div>
        </div>
      </section>

      {/* QUICK CONTACT TILES — phones for both offices + email + hours */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SITE.offices.map((o) => (
            <a
              key={o.id}
              href={o.phoneHref}
              data-testid={`contact-phone-${o.id}`}
              className="group bg-white border border-brand-line rounded-3xl p-7 hover:bg-brand hover:text-white transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl bg-brand-soft flex items-center justify-center group-hover:bg-white/15 transition-colors">
                  <Phone className="w-5 h-5 text-brand group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <ArrowUpRight className="w-5 h-5 text-brand-mute group-hover:text-white transition-colors" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-mute group-hover:text-white/70 transition-colors">
                {o.city} Office
              </p>
              <p className="font-display text-[1.6rem] lg:text-[1.85rem] mt-1.5 leading-tight group-hover:text-white transition-colors">
                {o.phone}
              </p>
              <p className="mt-2 text-sm text-brand-mute group-hover:text-white/80 transition-colors">
                Care enquiries &amp; recruitment
              </p>
            </a>
          ))}

          {/* EMAIL */}
          <a
            href={SITE.emailHref}
            data-testid={CONTACT_PAGE.email}
            className="group bg-white border border-brand-line rounded-3xl p-7 hover:bg-brand hover:text-white transition-all duration-300 hover:-translate-y-1"
          >
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-2xl bg-brand-soft flex items-center justify-center group-hover:bg-white/15 transition-colors">
                <Mail className="w-5 h-5 text-brand group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <ArrowUpRight className="w-5 h-5 text-brand-mute group-hover:text-white transition-colors" />
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-mute group-hover:text-white/70 transition-colors">
              Email
            </p>
            <p className="font-display text-base lg:text-lg mt-1.5 leading-tight break-all group-hover:text-white transition-colors">
              {SITE.email}
            </p>
            <p className="mt-2 text-sm text-brand-mute group-hover:text-white/80 transition-colors">
              Replies within one working day.
            </p>
          </a>

          {/* HOURS */}
          <div
            data-testid={CONTACT_PAGE.hours}
            className="bg-brand text-white rounded-3xl p-7 relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-white/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-[0.22em] font-semibold text-white/80">
                Opening hours
              </p>
              <ul className="mt-2 space-y-2">
                {SITE.hours.map((h) => (
                  <li key={h.label}>
                    <p className="font-display text-[15px] leading-tight text-white">
                      {h.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-white/75">
                      {h.label}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* OFFICES — addresses + maps */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Our offices
            </span>
            <h2 className="font-display text-3xl lg:text-4xl mt-4 leading-tight">
              Visit us in Doncaster or Leicester.
            </h2>
          </div>

          <div
            data-testid={CONTACT_PAGE.address}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
          >
            {SITE.offices.map((o) => (
              <article
                key={o.id}
                data-testid={`contact-office-${o.id}`}
                className="bg-white border border-brand-line rounded-3xl overflow-hidden flex flex-col"
              >
                <div className="p-7 lg:p-8">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-brand-soft flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-brand" strokeWidth={1.5} />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-mute">
                      {o.city} Office
                    </p>
                  </div>
                  <address className="not-italic mt-5 font-display text-xl lg:text-2xl leading-snug text-brand-ink">
                    {o.line1}
                    <br />
                    {o.line2}
                    <br />
                    {o.line3}
                    <br />
                    {o.postcode}
                  </address>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={o.phoneHref}
                      className="inline-flex items-center gap-2 rounded-full border border-brand-ink/15 bg-white text-brand-ink px-5 py-2.5 text-sm font-semibold hover:bg-brand-soft transition"
                    >
                      <Phone className="w-4 h-4" /> {o.phone}
                    </a>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${officeMapQuery(o)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5"
                    >
                      Get directions <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                <div className="border-t border-brand-line h-[260px] bg-brand-cream">
                  <iframe
                    title={`${o.city} office on the map`}
                    src={`https://www.google.com/maps?q=${officeMapQuery(o)}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-brand-line rounded-3xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
                  <span className="w-8 h-px bg-brand" /> Care enquiry
                </span>
                <h2 className="font-display text-3xl lg:text-4xl mt-4 leading-tight">
                  Tell us a little about the care you need.
                </h2>
                <p className="mt-4 text-brand-mute leading-relaxed">
                  Send us a short enquiry and a member of our team will be in
                  touch — usually within one working day.
                </p>
                <div className="mt-8">
                  <CqcBadge />
                </div>
              </div>

              <form
                data-testid={CONTACT_PAGE.form}
                onSubmit={handleSubmit}
                className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
                noValidate
              >
                <div className="sm:col-span-1">
                  <label htmlFor="name" className={labelCls}>Your name *</label>
                  <input id="name" type="text" required minLength={2} value={form.name} onChange={set("name")} data-testid={CONTACT_PAGE.formName} className={inputCls} placeholder="Jane Smith" autoComplete="name" />
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="phone" className={labelCls}>Phone *</label>
                  <input id="phone" type="tel" required value={form.phone} onChange={set("phone")} data-testid={CONTACT_PAGE.formPhone} className={inputCls} placeholder="07000 000000" autoComplete="tel" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="email" className={labelCls}>Email *</label>
                  <input id="email" type="email" required value={form.email} onChange={set("email")} data-testid={CONTACT_PAGE.formEmail} className={inputCls} placeholder="you@example.com" autoComplete="email" />
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="care_needed" className={labelCls}>Care needed *</label>
                  <select id="care_needed" required value={form.care_needed} onChange={set("care_needed")} data-testid={CONTACT_PAGE.formCare} className={inputCls + selectChevron}>
                    {CARE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="preferred_time" className={labelCls}>Preferred contact time</label>
                  <select id="preferred_time" value={form.preferred_time} onChange={set("preferred_time")} data-testid={CONTACT_PAGE.formTime} className={inputCls + selectChevron}>
                    {CONTACT_TIME_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className={labelCls}>Tell us more (optional)</label>
                  <textarea id="message" rows={5} value={form.message} onChange={set("message")} data-testid={CONTACT_PAGE.formMessage} className={`${inputCls} resize-y`} placeholder="A few details about who the care is for, what's needed, and when you'd like to start." />
                </div>

                {status === "success" && (
                  <div data-testid={CONTACT_PAGE.formSuccess} className="sm:col-span-2 flex items-start gap-3 rounded-2xl border border-brand bg-brand-soft px-5 py-4 text-brand-ink" role="status">
                    <CheckCircle2 className="w-5 h-5 text-brand mt-0.5 shrink-0" strokeWidth={2} />
                    <div>
                      <p className="font-semibold">Thanks — your enquiry has been sent.</p>
                      <p className="text-sm text-brand-mute mt-0.5">A member of our team will be in touch soon.</p>
                    </div>
                  </div>
                )}
                {status === "error" && (
                  <div data-testid={CONTACT_PAGE.formError} className="sm:col-span-2 flex items-start gap-3 rounded-2xl border border-red-300 bg-red-50 px-5 py-4 text-red-900" role="alert">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" strokeWidth={2} />
                    <div>
                      <p className="font-semibold">We couldn't send your enquiry.</p>
                      <p className="text-sm mt-0.5">{errorMsg}</p>
                    </div>
                  </div>
                )}

                <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mt-2">
                  <p className="text-xs text-brand-mute leading-relaxed sm:max-w-md">
                    By submitting this form you consent to Embrace Healthcare
                    contacting you about your enquiry. We never share your
                    details with third parties.
                  </p>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid={CONTACT_PAGE.formSubmit}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 w-full sm:w-auto"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Send enquiry
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
